import { FormEvent, useState, useEffect, useRef } from 'react';
import './BSearchInput.css';

interface BSearchInputProps {
  onSubmit: (query: string) => void;
  isLoading?: boolean;
  placeholder?: string;
  value?: string;
  onChange?: (value: string) => void;
  autoFocus?: boolean;
}

/**
 * Minimal rectangle search input with red submit button.
 * Can be controlled (value + onChange) or uncontrolled.
 */
export function BSearchInput({
  onSubmit,
  isLoading = false,
  placeholder = "describe the sound you're craving...",
  value: controlledValue,
  onChange,
  autoFocus = false,
}: BSearchInputProps) {
  const [internalValue, setInternalValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-focus when requested
  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  // Use controlled value if provided, otherwise internal state
  const value = controlledValue !== undefined ? controlledValue : internalValue;
  const setValue = onChange || setInternalValue;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (value.trim() && !isLoading) {
      onSubmit(value.trim());
    }
  };

  return (
    <form className="b-search-form" onSubmit={handleSubmit}>
      <input
        ref={inputRef}
        type="text"
        className="b-search-input"
        placeholder={placeholder}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={isLoading}
      />
      <button
        type="submit"
        className="b-search-btn"
        disabled={isLoading || !value.trim()}
      >
        {isLoading ? '...' : 'Discover'}
      </button>
    </form>
  );
}

// Export a function to set value from outside (for suggestion chips)
export function useSearchInputRef() {
  const [query, setQuery] = useState('');
  return { query, setQuery };
}
